import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ROLES, ROLE_LABELS } from '@kortex/shared';
import {
  BarChart,
  BarList,
  ConfirmDialog,
  Empty,
  ErrorState,
  Field,
  Kpi,
  Loading,
  Modal,
  Pager,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';
import { api, fieldErrors } from '../lib/api';
import { usePaged } from '../lib/usePaged';
import { needsSupportAccess, SupportAccessRequest } from '../components/privacy/SupportAccessRequest';
import { bytes, date, dateShort, dateTime, money, money0 } from '../lib/format';
import { DayBookReportView } from './DayBook';
import { BillingOptionsDrawer } from './BillingAdmin';

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface PlatformDashboard {
  tenants: {
    total: number;
    trialing: number;
    active: number;
    pastDue: number;
    suspended: number;
    expired: number;
    cancelled: number;
    newThisWeek: number;
    newThisMonth: number;
  };
  scale: { properties: number; users: number };
  revenue: { mrr: number; arr: number; payingTenants: number; averageRevenuePerTenant: number };
  planDistribution: Array<{ code: string; name: string; tenants: number; mrr: number }>;
  signupTrend: Array<{ day: string; count: number }>;
  trialsEndingSoon: Array<{
    tenantId: string;
    name: string;
    contactEmail: string | null;
    daysRemaining: number;
  }>;
  recentRegistrations: Array<{
    tenantId: string;
    name: string;
    city: string | null;
    contactEmail: string | null;
    source: string;
    status: string | null;
    properties: number;
    users: number;
    registeredAt: string;
  }>;
}

interface TenantRow {
  tenantId: string;
  name: string;
  source: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  gstin: string | null;
  propertyNames: string[];
  propertyCount: number;
  userCount: number;
  planCode: string | null;
  planName: string | null;
  priceMonthly: number;
  status: string;
  statusLabel: string;
  daysRemaining: number;
  registeredAt: string;
}

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  priceLabel: string | null;
  trialDays: number;
  maxProperties: number;
  maxUsers: number;
  features: string[];
  sortOrder: number;
  isPublic: boolean;
  isActive: boolean;
  subscribers: number;
}

/** A new plan's starting point. `code` is only ever set here - it is what
 *  subscriptions and the registration page refer to, so it cannot be renamed
 *  afterwards (the update schema has no field for it). */
const BLANK_PLAN: Plan = {
  id: '',
  code: '',
  name: '',
  description: '',
  priceMonthly: 0,
  priceLabel: '',
  trialDays: 0,
  maxProperties: 1,
  maxUsers: 5,
  features: [],
  sortOrder: 0,
  isPublic: true,
  isActive: true,
  subscribers: 0,
};

/* ------------------------------------------------------------------ */
/* Dashboard                                                            */
/* ------------------------------------------------------------------ */

export function PlatformDashboardPage() {
  const query = useQuery({
    queryKey: ['platform', 'dashboard'],
    queryFn: () => api.get<PlatformDashboard>('/api/platform/dashboard'),
  });
  // Five at a time on a phone, where each registration is a card; the desktop
  // table has always shown all of them.
  const registrations = usePaged(query.data?.recentRegistrations ?? [], { phone: 5, desktop: 10 });

  if (query.isLoading) return <Loading label="Loading the platform…" />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const data = query.data!;

  return (
    <div className="stack">
      <div className="kpis">
        <Kpi
          label="Hotels registered"
          value={String(data.tenants.total)}
          foot={`${data.tenants.newThisWeek} this week · ${data.tenants.newThisMonth} this month`}
        />
        <Kpi
          label="Monthly recurring revenue"
          value={money0(data.revenue.mrr)}
          foot={`${data.revenue.payingTenants} paying · ${money0(data.revenue.arr)} annualised`}
          tone="blue"
        />
        <Kpi
          label="On trial"
          value={String(data.tenants.trialing)}
          foot={`${data.trialsEndingSoon.length} ending within a week`}
          tone={data.trialsEndingSoon.length ? 'amber' : ''}
        />
        <Kpi
          label="Needs attention"
          value={String(data.tenants.suspended + data.tenants.expired)}
          foot={`${data.tenants.suspended} suspended · ${data.tenants.expired} expired`}
          tone={data.tenants.suspended + data.tenants.expired ? 'red' : ''}
        />
        <Kpi
          label="Properties"
          value={String(data.scale.properties)}
          foot={`${data.scale.users} hotel users`}
        />
        <Kpi
          label="Average revenue"
          value={money0(data.revenue.averageRevenuePerTenant)}
          foot="Per paying hotel, monthly"
        />
      </div>

      <div className="grid-32">
        <div className="card">
          <div className="card-head">
            <h2>Signups, last 30 days</h2>
            <span className="chip">{data.tenants.newThisMonth} total</span>
          </div>
          <div className="card-body">
            <BarChart
              data={data.signupTrend.map((d) => ({ label: dateShort(d.day), value: d.count }))}
              height={190}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Plan mix</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={data.planDistribution.map((p) => ({
                label: `${p.name} · ${p.tenants} ${p.tenants === 1 ? 'hotel' : 'hotels'}`,
                value: p.mrr,
              }))}
              format={money0}
            />
          </div>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Latest registrations</h2>
          </div>
          {data.recentRegistrations.length === 0 ? (
            <Empty message="No hotels have registered yet" />
          ) : (
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>City</th>
                    <th>Contact</th>
                    <th>Registered</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.rows.map((row) => (
                    <tr key={row.tenantId}>
                      <td>
                        <div className="b">{row.name}</div>
                        <div className="xs muted">
                          {row.properties} property · {row.users} users
                        </div>
                      </td>
                      <td className="dim" data-label="City">
                        {row.city ?? '—'}
                      </td>
                      <td className="small" data-label="Contact">
                        {row.contactEmail ?? '—'}
                      </td>
                      <td className="nowrap" data-label="Registered">
                        {date(row.registeredAt)}
                      </td>
                      <td data-label="Status">
                        {row.status ? <StatusChip status={row.status} /> : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager {...registrations} noun="registrations" onPage={registrations.setPage} />
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Trials ending soon</h2>
          </div>
          {data.trialsEndingSoon.length === 0 ? (
            <Empty icon="✓" message="No trial ends in the next seven days" />
          ) : (
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Hotel</th>
                    <th>Contact</th>
                    <th className="num">Days left</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trialsEndingSoon.map((row) => (
                    <tr key={row.tenantId}>
                      <td className="b">{row.name}</td>
                      <td className="small" data-label="Contact">
                        {row.contactEmail ?? '—'}
                      </td>
                      <td className="num" data-label="Days left">
                        <span className={`chip ${row.daysRemaining <= 2 ? 'danger' : 'warn'}`}>
                          {row.daysRemaining}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hotels                                                               */
/* ------------------------------------------------------------------ */

export function PlatformHotelsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [openTenant, setOpenTenant] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['platform', 'tenants', q, status],
    queryFn: () =>
      api.get<{ total: number; entries: TenantRow[] }>(
        `/api/platform/tenants?${new URLSearchParams({
          limit: '200',
          ...(q ? { q } : {}),
          ...(status ? { status } : {}),
        }).toString()}`,
      ),
  });

  // Ten at a time on a phone, where each hotel is a card; the desktop table shows
  // the whole list, as it always has.
  const hotels = usePaged(query.data?.entries ?? [], { phone: 10, desktop: Infinity });

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div className="row gap-6 wrap">
            <div className="search" style={{ width: 260, maxWidth: '100%' }}>
              <input
                className="input"
                placeholder="Hotel, city or contact email"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ width: 170, maxWidth: '100%' }}
            >
              <option value="">Every status</option>
              <option value="TRIALING">On trial</option>
              <option value="ACTIVE">Active</option>
              <option value="PAST_DUE">Payment overdue</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="EXPIRED">Expired</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </div>
          <span className="chip">{query.data?.entries.length ?? 0} shown</span>
        </div>

        {query.isLoading ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data!.entries.length === 0 ? (
          <Empty message="No hotels match" />
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Contact</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="num">Days left</th>
                  <th className="num">Scale</th>
                  <th>Registered</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {hotels.rows.map((row) => (
                  <tr
                    key={row.tenantId}
                    className="clickable"
                    onClick={() => setOpenTenant(row.tenantId)}
                  >
                    <td>
                      <div className="b">{row.name}</div>
                      <div className="xs muted">
                        {row.city ?? '—'}
                        {row.gstin ? ` · ${row.gstin}` : ''}
                        {row.source === 'SELF_SERVICE' ? ' · self-registered' : ''}
                      </div>
                    </td>
                    <td className="small" data-label="Contact">
                      {row.contactEmail ?? '—'}
                      {row.contactPhone ? <div className="xs muted">{row.contactPhone}</div> : null}
                    </td>
                    <td data-label="Plan">
                      <div className="b">{row.planName ?? '—'}</div>
                      {row.priceMonthly > 0 ? (
                        <div className="xs muted">{money0(row.priceMonthly)}/mo</div>
                      ) : null}
                    </td>
                    <td data-label="Status">
                      <StatusChip status={row.status} />
                    </td>
                    <td className="num" data-label="Days left">
                      {row.daysRemaining || '—'}
                    </td>
                    <td className="num dim" data-label="Scale">
                      {row.propertyCount}p · {row.userCount}u
                    </td>
                    <td className="nowrap" data-label="Registered">
                      {date(row.registeredAt)}
                    </td>
                    <td className="right" data-label="">
                      <button
                        className="btn ghost sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenTenant(row.tenantId);
                        }}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pager {...hotels} noun="hotels" onPage={hotels.setPage} />
      </div>

      {openTenant ? (
        <TenantDrawer tenantId={openTenant} onClose={() => setOpenTenant(null)} />
      ) : null}
    </div>
  );
}

interface TenantDetail {
  tenantId: string;
  name: string;
  source: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  registeredAt: string;
  properties: Array<{
    id: string;
    name: string;
    code: string;
    city: string;
    email: string;
    phone: string;
    gstin: string;
    rooms: number;
    employees: number;
  }>;
  users: Array<{
    id: string;
    name: string;
    email: string;
    phone: string | null;
    isActive: boolean;
    isLocked: boolean;
    mustChangePassword: boolean;
    twoFactorEnabled: boolean;
    lastLoginAt: string | null;
    memberships: Array<{ propertyId: string; propertyName: string; role: string }>;
  }>;
  usage: { invoices: number; lastInvoiceAt: string | null };
  subscription: {
    planCode: string;
    planName: string;
    priceMonthly: number;
    status: string;
    statusLabel: string;
    daysRemaining: number;
    endsAt: string | null;
    events: Array<{
      id: string;
      type: string;
      fromPlanCode: string | null;
      toPlanCode: string | null;
      note: string | null;
      createdAt: string;
    }>;
  } | null;
}

function TenantDrawer({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [action, setAction] = useState('CHANGE_PLAN');
  const [planCode, setPlanCode] = useState('SILVER');
  const [days, setDays] = useState(15);
  const [months, setMonths] = useState(1);
  const [note, setNote] = useState('');
  const [managingUser, setManagingUser] = useState<TenantDetail['users'][number] | null>(null);
  const [dayBookProperty, setDayBookProperty] = useState<{ id: string; name: string } | null>(
    null,
  );

  const detail = useQuery({
    queryKey: ['platform', 'tenant', tenantId],
    queryFn: () => api.get<TenantDetail>(`/api/platform/tenants/${tenantId}`),
  });
  const plans = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get<Plan[]>('/api/platform/plans'),
  });

  const act = useMutation({
    mutationFn: () =>
      api.post(`/api/platform/tenants/${tenantId}/subscription`, {
        action,
        ...(action === 'CHANGE_PLAN' ? { planCode } : {}),
        ...(action === 'EXTEND_TRIAL' ? { days } : {}),
        ...(action === 'EXTEND_PLAN' || action === 'ACTIVATE' ? { months } : {}),
        ...(note ? { note } : {}),
      }),
    onSuccess: () => {
      push('Subscription updated', 'ok');
      setNote('');
      void client.invalidateQueries({ queryKey: ['platform'] });
    },
    onError: fail,
  });

  return (
    <>
    <Modal title={detail.data?.name ?? 'Hotel'} onClose={onClose} size="wide">
      {detail.isLoading ? (
        <Loading />
      ) : needsSupportAccess(detail.error) ? (
        <SupportAccessRequest tenantId={tenantId} onOpened={() => void detail.refetch()} />
      ) : detail.error ? (
        <ErrorState error={detail.error} />
      ) : (
        <div className="stack">
          <div className="kpis">
            <Kpi
              label="Plan"
              value={detail.data!.subscription?.planName ?? '—'}
              foot={
                detail.data!.subscription
                  ? `${money0(detail.data!.subscription.priceMonthly)}/month`
                  : ''
              }
            />
            <Kpi
              label="Status"
              value={detail.data!.subscription?.statusLabel ?? '—'}
              foot={
                detail.data!.subscription?.endsAt
                  ? `until ${date(detail.data!.subscription.endsAt)}`
                  : ''
              }
            />
            <Kpi
              label="Invoices raised"
              value={String(detail.data!.usage.invoices)}
              foot={
                detail.data!.usage.lastInvoiceAt
                  ? `last ${dateShort(detail.data!.usage.lastInvoiceAt)}`
                  : 'never billed'
              }
              tone={detail.data!.usage.invoices === 0 ? 'amber' : ''}
            />
            <Kpi
              label="Registered"
              value={dateShort(detail.data!.registeredAt)}
              foot={
                detail.data!.source === 'SELF_SERVICE' ? 'Self-registered' : 'Created by SARTHI'
              }
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Contact</h3>
            </div>
            <div className="card-body small">
              <div>{detail.data!.contactName ?? '—'}</div>
              <div className="muted">
                {detail.data!.contactEmail ?? '—'} · {detail.data!.contactPhone ?? '—'}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Properties</h3>
            </div>
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>City</th>
                    <th>GSTIN</th>
                    <th>Email</th>
                    <th className="num">Rooms</th>
                    <th className="num">Staff</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.properties.map((p) => (
                    <tr key={p.id}>
                      <td className="b">
                        {p.name} <span className="xs muted mono">{p.code}</span>
                      </td>
                      <td>{p.city}</td>
                      <td className="mono xs">{p.gstin}</td>
                      <td className="small">{p.email}</td>
                      <td className="num">{p.rooms}</td>
                      <td className="num">{p.employees}</td>
                      <td>
                        <button
                          className="btn sm"
                          onClick={() => setDayBookProperty({ id: p.id, name: p.name })}
                        >
                          Day Book
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Users</h3>
              <span className="small muted">Reset a password or 2FA, or edit access - for when a hotel needs help</span>
            </div>
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Status</th>
                    <th>Last sign-in</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.users.map((u) => (
                    <tr key={u.id}>
                      <td className="b">{u.name}</td>
                      <td className="small">{u.email}</td>
                      <td className="small">
                        {[...new Set(u.memberships.map((m) => m.role))].join(', ') || '—'}
                      </td>
                      <td>
                        <span className={`chip ${u.isActive ? 'ok' : ''}`}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {u.isLocked ? <span className="chip warn">Locked</span> : null}
                      </td>
                      <td className="nowrap dim">{u.lastLoginAt ? dateTime(u.lastLoginAt) : 'never'}</td>
                      <td className="right">
                        <button className="btn ghost sm" onClick={() => setManagingUser(u)}>
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Manage subscription</h3>
            </div>
            <div className="card-body col gap-16">
              <div className="form-grid three">
                <Select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
                  <option value="CHANGE_PLAN">Change plan</option>
                  <option value="EXTEND_TRIAL">Extend trial</option>
                  <option value="EXTEND_PLAN">Extend plan</option>
                  <option value="ACTIVATE">Activate</option>
                  <option value="SUSPEND">Suspend</option>
                  <option value="CANCEL">Cancel</option>
                </Select>
                {action === 'CHANGE_PLAN' ? (
                  <Select
                    label="Plan"
                    value={planCode}
                    onChange={(e) => setPlanCode(e.target.value)}
                  >
                    {(plans.data ?? []).map((p) => (
                      <option key={p.code} value={p.code}>
                        {p.name} — {p.priceMonthly === 0 ? 'free' : `${money0(p.priceMonthly)}/mo`}
                      </option>
                    ))}
                  </Select>
                ) : null}
                {action === 'EXTEND_TRIAL' ? (
                  <TextInput
                    label="Days to add"
                    type="number"
                    min={1}
                    max={365}
                    value={days}
                    onChange={(e) => setDays(Number(e.target.value))}
                  />
                ) : null}
                {action === 'EXTEND_PLAN' || action === 'ACTIVATE' ? (
                  <TextInput
                    label="Months"
                    type="number"
                    min={1}
                    max={36}
                    hint={
                      action === 'EXTEND_PLAN'
                        ? 'Added to whatever is left, not from today'
                        : 'From today'
                    }
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                  />
                ) : null}
                <TextInput
                  label="Note"
                  placeholder="Why"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
              <button className="btn primary" disabled={act.isPending} onClick={() => act.mutate()}>
                {act.isPending ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h3>Subscription history</h3>
            </div>
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Plan</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.data!.subscription?.events ?? []).map((e) => (
                    <tr key={e.id}>
                      <td className="nowrap">{dateTime(e.createdAt)}</td>
                      <td>
                        <span className="chip">{e.type.replace(/_/g, ' ').toLowerCase()}</span>
                      </td>
                      <td className="small">
                        {e.fromPlanCode && e.fromPlanCode !== e.toPlanCode
                          ? `${e.fromPlanCode} → ${e.toPlanCode}`
                          : (e.toPlanCode ?? '—')}
                      </td>
                      <td className="small dim">{e.note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>

    {managingUser ? (
      <TenantUserModal
        tenantId={tenantId}
        user={managingUser}
        properties={detail.data?.properties ?? []}
        onClose={() => setManagingUser(null)}
        onSaved={() => {
          setManagingUser(null);
          void client.invalidateQueries({ queryKey: ['platform', 'tenant', tenantId] });
        }}
      />
    ) : null}

    {dayBookProperty ? (
      <Modal
        title={`Day Book - ${dayBookProperty.name}`}
        size="wide"
        onClose={() => setDayBookProperty(null)}
      >
        <DayBookReportView endpoint={`/api/platform/properties/${dayBookProperty.id}/day-book`} />
      </Modal>
    ) : null}
    </>
  );
}

/**
 * Superadmin support for one of a hotel's users - reset password, reset
 * 2FA, edit name/phone/active-status/property access. Deliberately no
 * email field: email is the global sign-in identity, out of scope here
 * (docs/platform - the same reasoning as why hotel Owners can't edit it
 * either, in Admin.tsx's own UserModal).
 */
function TenantUserModal({
  tenantId,
  user,
  properties,
  onClose,
  onSaved,
}: {
  tenantId: string;
  user: TenantDetail['users'][number];
  properties: TenantDetail['properties'];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push, fail } = useToast();
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [isActive, setIsActive] = useState(user.isActive);
  const [memberships, setMemberships] = useState(
    user.memberships.map((m) => ({ propertyId: m.propertyId, role: m.role })),
  );
  const [newPassword, setNewPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmingTwoFactorReset, setConfirmingTwoFactorReset] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/platform/tenants/${tenantId}/users/${user.id}`, { name, phone, isActive, memberships }),
    onSuccess: () => {
      push('User updated', 'ok');
      onSaved();
    },
    onError: (error) => {
      setErrors(fieldErrors(error));
      fail(error);
    },
  });

  const resetPassword = useMutation({
    mutationFn: () =>
      api.post(`/api/platform/tenants/${tenantId}/users/${user.id}/reset-password`, { newPassword }),
    onSuccess: () => {
      push('Password reset — the user must change it at next sign-in', 'ok');
      setNewPassword('');
    },
    onError: fail,
  });

  const resetTwoFactor = useMutation({
    mutationFn: () => api.post(`/api/platform/tenants/${tenantId}/users/${user.id}/reset-2fa`),
    onSuccess: () => {
      push('Two-step verification reset for this account', 'ok');
      setConfirmingTwoFactorReset(false);
      onSaved();
    },
    onError: (error) => {
      setConfirmingTwoFactorReset(false);
      fail(error);
    },
  });

  return (
    <>
      <Modal
        title={`Manage ${user.name}`}
        onClose={onClose}
        footer={
          <>
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
            {user.twoFactorEnabled ? (
              <button className="btn" onClick={() => setConfirmingTwoFactorReset(true)}>
                Reset 2FA
              </button>
            ) : null}
            {newPassword ? (
              <button
                className="btn"
                disabled={resetPassword.isPending}
                onClick={() => resetPassword.mutate()}
              >
                Reset password
              </button>
            ) : null}
            <button
              className="btn primary"
              disabled={!name.trim() || memberships.length === 0 || save.isPending}
              onClick={() => save.mutate()}
            >
              Save changes
            </button>
          </>
        }
      >
        <div className="col gap-16">
          <div className="small muted">{user.email}</div>
          <div className="form-grid">
            <TextInput
              className="span2"
              label="Full name"
              value={name}
              error={errors['name']}
              onChange={(e) => setName(e.target.value)}
            />
            <TextInput
              className="span2"
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <TextInput
              className="span2"
              label="New password (optional)"
              type="text"
              value={newPassword}
              hint="Fill this in and press Reset password to force a new one"
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Property access</h2>
              <button
                className="btn sm"
                onClick={() =>
                  setMemberships([...memberships, { propertyId: properties[0]?.id ?? '', role: 'CASHIER' }])
                }
              >
                + Grant access
              </button>
            </div>
            <div className="card-body col gap-6">
              {memberships.map((membership, index) => (
                <div className="row gap-6" key={index}>
                  <select
                    className="select"
                    style={{ flex: 2 }}
                    value={membership.propertyId}
                    onChange={(e) =>
                      setMemberships(
                        memberships.map((m, i) => (i === index ? { ...m, propertyId: e.target.value } : m)),
                      )
                    }
                  >
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className="select"
                    style={{ flex: 1 }}
                    value={membership.role}
                    onChange={(e) =>
                      setMemberships(
                        memberships.map((m, i) => (i === index ? { ...m, role: e.target.value } : m)),
                      )
                    }
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn ghost icon sm"
                    disabled={memberships.length === 1}
                    onClick={() => setMemberships(memberships.filter((_, i) => i !== index))}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            Account is active (turning this off signs the user out everywhere)
          </label>
        </div>
      </Modal>

      {confirmingTwoFactorReset ? (
        <ConfirmDialog
          title="Reset two-step verification?"
          message={`${user.name} will need to set up two-step verification again from scratch - their current recovery codes stop working immediately.`}
          confirmLabel="Reset 2FA"
          danger
          busy={resetTwoFactor.isPending}
          onConfirm={() => resetTwoFactor.mutate()}
          onCancel={() => setConfirmingTwoFactorReset(false)}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Plans                                                                */
/* ------------------------------------------------------------------ */

export function PlatformPlansPage() {
  const client = useQueryClient();
  const { push, fail } = useToast();
  const [editing, setEditing] = useState<Plan | null>(null);
  const [removing, setRemoving] = useState<Plan | null>(null);
  /** Where the hotels on a plan being deleted should land. */
  const [reassignTo, setReassignTo] = useState('');
  const [periodsFor, setPeriodsFor] = useState<{ id: string; name: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const query = useQuery({
    queryKey: ['platform', 'plans'],
    queryFn: () => api.get<Plan[]>('/api/platform/plans'),
  });

  const refresh = () => client.invalidateQueries({ queryKey: ['platform', 'plans'] });
  const close = () => {
    setEditing(null);
    setErrors({});
  };

  const save = useMutation({
    mutationFn: (plan: Plan) => {
      const payload = {
        name: plan.name,
        description: plan.description ?? '',
        priceMonthly: Number(plan.priceMonthly),
        priceLabel: plan.priceLabel ?? '',
        trialDays: Number(plan.trialDays),
        maxProperties: Number(plan.maxProperties),
        maxUsers: Number(plan.maxUsers),
        features: plan.features,
        sortOrder: Number(plan.sortOrder),
        isPublic: plan.isPublic,
        isActive: plan.isActive,
      };
      // `code` goes only on create: it is the key subscriptions and the
      // registration page resolve a plan by, so it is fixed once issued.
      return plan.id
        ? api.patch(`/api/platform/plans/${plan.id}`, payload)
        : api.post('/api/platform/plans', { ...payload, code: plan.code });
    },
    onSuccess: (_data, plan) => {
      push(plan.id ? 'Plan updated' : `${plan.name} created`, 'ok');
      close();
      void refresh();
    },
    onError: (caught) => {
      setErrors(fieldErrors(caught));
      fail(caught);
    },
  });

  const remove = useMutation({
    mutationFn: (input: { plan: Plan; reassignTo: string | null }) =>
      api.delete<{ moved: number; movedTo: string | null }>(
        `/api/platform/plans/${input.plan.id}` +
          (input.reassignTo ? `?reassignTo=${input.reassignTo}` : ''),
      ),
    onSuccess: (result) => {
      push(
        result?.moved
          ? `Plan deleted; ${result.moved} hotel(s) moved to ${result.movedTo}`
          : 'Plan deleted',
        'ok',
      );
      setRemoving(null);
      setReassignTo('');
      void refresh();
    },
    onError: (caught) => {
      // Kept open on failure: the destination the superadmin picked is still
      // on screen to correct, rather than having to start the dialog again.
      fail(caught);
    },
  });

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;

  const plans = query.data ?? [];
  const publicCount = plans.filter((p) => p.isPublic && p.isActive).length;

  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <div>
            <h2>Subscription plans</h2>
            <div className="small muted">
              What a hotel sees on the registration page, and what it is charged.{' '}
              {publicCount === 0
                ? 'Nothing is offered at registration right now.'
                : `${publicCount} offered at registration.`}
            </div>
          </div>
          <button className="btn primary" onClick={() => setEditing({ ...BLANK_PLAN })}>
            + New plan
          </button>
        </div>

        {plans.length === 0 ? (
          <Empty message="No plans yet — nobody can complete registration until one is offered." />
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th className="num">#</th>
                  <th>Plan</th>
                  <th className="num">Price / month</th>
                  <th className="num">Free trial</th>
                  {/* "per hotel" and "subscribers" spelled out: a limit of 2
                      properties sitting next to a count of 3 reads as a
                      contradiction otherwise, when they measure different
                      things entirely. */}
                  <th className="num" title="What one hotel on this plan may create">
                    Limits / hotel
                  </th>
                  <th>Features</th>
                  <th className="num" title="Hotels subscribed to this plan">
                    Subscribers
                  </th>
                  <th>Visibility</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td className="num dim stack-hide">{plan.sortOrder}</td>
                    <td className="stack-title">
                      <div className="b">
                        {plan.name} <span className="xs muted mono">{plan.code}</span>
                      </div>
                      {plan.description ? <div className="xs muted">{plan.description}</div> : null}
                    </td>
                    <td className="num b">
                      {plan.priceLabel
                        ? plan.priceLabel
                        : plan.priceMonthly === 0
                          ? 'Free'
                          : money(plan.priceMonthly)}
                    </td>
                    <td className="num">{plan.trialDays ? `${plan.trialDays} days` : '—'}</td>
                    <td
                      className="num dim"
                      title={`Each hotel on this plan may create up to ${plan.maxProperties} propert${plan.maxProperties === 1 ? 'y' : 'ies'} and ${plan.maxUsers} users`}
                    >
                      {plan.maxProperties} prop · {plan.maxUsers} users
                    </td>
                    <td className="xs muted">
                      {plan.features.length > 0 ? `${plan.features.length} listed` : '—'}
                    </td>
                    <td className="num">{plan.subscribers}</td>
                    <td>
                      <span className={`chip ${plan.isActive ? 'ok' : 'danger'}`}>
                        {plan.isActive ? 'Active' : 'Retired'}
                      </span>
                      {plan.isPublic ? (
                        <span className="chip info" style={{ marginLeft: 4 }}>
                          Public
                        </span>
                      ) : null}
                    </td>
                    <td className="right">
                      <button
                        className="btn ghost sm"
                        onClick={() => setPeriodsFor({ id: plan.id, name: plan.name })}
                      >
                        Periods
                      </button>
                      <button
                        className="btn ghost sm"
                        onClick={() => setEditing({ ...plan })}
                        style={{ marginLeft: 4 }}
                      >
                        Edit
                      </button>
                      {/* Every plan can go. One with hotels on it asks where
                          to move them first - see the dialog below. */}
                      <button
                        className="btn ghost sm danger"
                        title={
                          plan.subscribers > 0
                            ? `${plan.subscribers} hotel(s) are on this plan — you will be asked where to move them`
                            : undefined
                        }
                        onClick={() => {
                          setRemoving(plan);
                          setReassignTo('');
                        }}
                        style={{ marginLeft: 4 }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <Modal
          title={editing.id ? `Edit ${editing.name}` : 'New plan'}
          onClose={close}
          footer={
            <>
              <button className="btn" onClick={close}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={save.isPending}
                onClick={() => save.mutate(editing)}
              >
                {save.isPending ? 'Saving…' : editing.id ? 'Save plan' : 'Create plan'}
              </button>
            </>
          }
        >
          <div className="form-grid">
            {editing.id ? null : (
              <TextInput
                label="Code"
                hint="Permanent. Letters, digits, hyphen or underscore."
                placeholder="ENTERPRISE"
                error={errors.code}
                value={editing.code}
                onChange={(e) =>
                  setEditing({ ...editing, code: e.target.value.toUpperCase().trim() })
                }
              />
            )}
            <TextInput
              label="Name"
              error={errors.name}
              placeholder="Enterprise"
              value={editing.name}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
            <TextInput
              label="Price per month (₹)"
              type="number"
              min={0}
              error={errors.priceMonthly}
              value={editing.priceMonthly}
              onChange={(e) => setEditing({ ...editing, priceMonthly: Number(e.target.value) })}
            />
            <TextInput
              label="Price label"
              hint='Replaces the price on the card, e.g. "Custom". Blank shows the figure.'
              placeholder="Custom"
              error={errors.priceLabel}
              value={editing.priceLabel ?? ''}
              onChange={(e) => setEditing({ ...editing, priceLabel: e.target.value })}
            />
            <TextInput
              label="Free trial (days)"
              type="number"
              min={0}
              max={365}
              hint="Applies to hotels registering from now on."
              error={errors.trialDays}
              value={editing.trialDays}
              onChange={(e) => setEditing({ ...editing, trialDays: Number(e.target.value) })}
            />
            <TextInput
              label="Order"
              type="number"
              min={0}
              max={999}
              hint="Lowest first, on the registration page."
              error={errors.sortOrder}
              value={editing.sortOrder}
              onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
            />
            <TextInput
              label="Max properties"
              type="number"
              min={1}
              error={errors.maxProperties}
              value={editing.maxProperties}
              onChange={(e) => setEditing({ ...editing, maxProperties: Number(e.target.value) })}
            />
            <TextInput
              label="Max users"
              type="number"
              min={1}
              error={errors.maxUsers}
              value={editing.maxUsers}
              onChange={(e) => setEditing({ ...editing, maxUsers: Number(e.target.value) })}
            />
            <div className="span2">
              <TextInput
                label="Description"
                error={errors.description}
                placeholder="For a single hotel running day to day."
                value={editing.description ?? ''}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              />
            </div>
            <div className="span2">
              <Field
                label="Features"
                hint="One per line, up to 20 — these are the ticks on the plan card."
                error={errors.features}
                id="plan-features"
              >
                <textarea
                  id="plan-features"
                  className="input tall"
                  rows={5}
                  placeholder={'All modules\nUp to 3 properties\nPriority support'}
                  value={editing.features.join('\n')}
                  onChange={(e) =>
                    setEditing({
                      // Split on write, join on read: the box is the source of
                      // truth while it is open, so a blank line being typed in
                      // the middle does not vanish under the cursor.
                      ...editing,
                      features: e.target.value.split('\n'),
                    })
                  }
                  onBlur={() =>
                    setEditing((current) =>
                      current
                        ? {
                            ...current,
                            features: current.features.map((f) => f.trim()).filter(Boolean),
                          }
                        : current,
                    )
                  }
                />
              </Field>
            </div>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.isPublic}
                onChange={(e) => setEditing({ ...editing, isPublic: e.target.checked })}
              />
              Offered on the registration page
            </label>
            <label className="row gap-6">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
        </Modal>
      ) : null}

      {periodsFor ? (
        <BillingOptionsDrawer
          planId={periodsFor.id}
          planName={periodsFor.name}
          onClose={() => setPeriodsFor(null)}
        />
      ) : null}

      {removing && removing.subscribers === 0 ? (
        <ConfirmDialog
          title={`Delete ${removing.name}?`}
          message={`The ${removing.code} plan will be removed for good. Nobody is on it, so nothing is billed differently — but if you only want it off the registration page, untick "Offered on the registration page" instead.`}
          confirmLabel="Delete plan"
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate({ plan: removing, reassignTo: null })}
          onCancel={() => setRemoving(null)}
        />
      ) : null}

      {/* A plan with hotels on it needs somewhere to put them, so this asks
          rather than assuming. Nothing is deleted until a destination is
          chosen - the confirm button stays disabled without one. */}
      {removing && removing.subscribers > 0 ? (
        <Modal
          title={`Delete ${removing.name}?`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn" onClick={() => setRemoving(null)}>
                Cancel
              </button>
              <button
                className="btn danger"
                disabled={!reassignTo || remove.isPending}
                onClick={() => remove.mutate({ plan: removing, reassignTo })}
              >
                {remove.isPending
                  ? 'Moving…'
                  : `Move ${removing.subscribers} and delete`}
              </button>
            </>
          }
        >
          <div className="col gap-16">
            <p className="small">
              <b>
                {removing.subscribers} hotel(s) are on {removing.name}.
              </b>{' '}
              Deleting it moves them to the plan you pick below. Their
              subscriptions, invoices and payment history are kept — only the
              plan changes, and each one records the move.
            </p>

            <Select
              label="Move these hotels to"
              value={reassignTo}
              onChange={(e) => setReassignTo(e.target.value)}
            >
              <option value="">Choose a plan…</option>
              {plans
                .filter((candidate) => candidate.id !== removing.id && candidate.isActive)
                .map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {/* The code, not just the name: two plans may well share a
                        name, and picking the wrong one silently rewrites what
                        these hotels are entitled to. */}
                    {candidate.name} ({candidate.code}) — {candidate.maxProperties} propert
                    {candidate.maxProperties === 1 ? 'y' : 'ies'}, {candidate.maxUsers} users
                  </option>
                ))}
            </Select>

            <p className="xs muted">
              The destination plan&rsquo;s limits apply from the moment they move, so a hotel
              already past them keeps what it has but cannot add more. Where the destination
              sells the same billing period they were on, they stay on it. If you only want
              this plan off the registration page, close this and untick &ldquo;Offered on the
              registration page&rdquo; instead.
            </p>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Reports                                                              */
/* ------------------------------------------------------------------ */

export function PlatformReportsPage() {
  const [days, setDays] = useState(30);
  const client = useQueryClient();
  const { push, fail } = useToast();

  const signups = useQuery({
    queryKey: ['platform', 'signups', days],
    queryFn: () =>
      api.get<{
        total: number;
        byDay: Array<{ day: string; count: number }>;
        bySource: Array<{ source: string; count: number }>;
      }>(`/api/platform/reports/signups?days=${days}`),
  });
  const revenue = useQuery({
    queryKey: ['platform', 'revenue'],
    queryFn: () =>
      api.get<{
        mrr: number;
        arr: number;
        byPlan: Array<{ code: string; name: string; tenants: number; paying: number; mrr: number }>;
        funnel: { trialing: number; converted: number; conversionPct: number; churned: number };
      }>('/api/platform/reports/revenue'),
  });
  const usage = useQuery({
    queryKey: ['platform', 'usage'],
    queryFn: () =>
      api.get<{
        dormantCount: number;
        entries: Array<{
          tenantId: string;
          name: string;
          planCode: string | null;
          status: string;
          users: number;
          properties: number;
          rooms: number;
          invoices30d: number;
          lastInvoiceAt: string | null;
          dormant: boolean;
        }>;
      }>('/api/platform/reports/usage'),
  });
  // Estimated, cached, never computed live - see docs/platform/storage-usage.md.
  // A single shared database backs every hotel, so there is no exact
  // per-hotel byte count to read; this sums row sizes grouped by property,
  // which "Recompute now" (below) re-runs on demand.
  const storage = useQuery({
    queryKey: ['platform', 'storage-usage'],
    queryFn: () =>
      api.get<{
        totalBytes: number;
        neverComputedCount: number;
        entries: Array<{
          tenantId: string;
          name: string;
          totalBytes: number;
          computedAt: string | null;
          properties: Array<{
            id: string;
            name: string;
            estimatedBytes: number | null;
            computedAt: string | null;
          }>;
        }>;
      }>('/api/platform/storage-usage'),
  });
  const refreshStorage = useMutation({
    mutationFn: () =>
      api.post<{ properties: number; tables: number; totalBytes: number; durationMs: number }>(
        '/api/platform/storage-usage/refresh',
      ),
    onSuccess: (result) => {
      push(
        `Recomputed ${result.properties} hotels across ${result.tables} tables in ${(result.durationMs / 1000).toFixed(1)}s`,
        'ok',
      );
      void client.invalidateQueries({ queryKey: ['platform', 'storage-usage'] });
    },
    onError: fail,
  });
  const couponReport = useQuery({
    queryKey: ['platform', 'coupon-report', days],
    queryFn: () =>
      api.get<{
        counts: {
          total: number;
          active: number;
          redeemed: number;
          expired: number;
          exhausted: number;
          paused: number;
          cancelled: number;
        };
        redemptionRatePct: number;
        freeMonthsGranted: number;
        byDay: Array<{ day: string; count: number }>;
        topCampaigns: Array<{ name: string; redeemed: number; generated: number; redemptionRatePct: number }>;
      }>(`/api/platform/reports/coupons?days=${days}`),
  });

  if (signups.isLoading || revenue.isLoading || usage.isLoading || storage.isLoading || couponReport.isLoading)
    return <Loading label="Building reports…" />;

  return (
    <div className="stack">
      <div className="row between wrap gap-6">
        <div className="seg">
          {[7, 30, 90].map((d) => (
            <button key={d} className={d === days ? 'active' : ''} onClick={() => setDays(d)}>
              Last {d} days
            </button>
          ))}
        </div>
        <button className="btn no-print" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="kpis">
        <Kpi
          label="Signups"
          value={String(signups.data!.total)}
          foot={`in the last ${days} days`}
        />
        <Kpi
          label="MRR"
          value={money0(revenue.data!.mrr)}
          foot={`${money0(revenue.data!.arr)} annualised`}
          tone="blue"
        />
        <Kpi
          label="Trial conversion"
          value={`${revenue.data!.funnel.conversionPct}%`}
          foot={`${revenue.data!.funnel.converted} converted · ${revenue.data!.funnel.trialing} on trial`}
        />
        <Kpi
          label="Dormant hotels"
          value={String(usage.data!.dormantCount)}
          foot="Registered but never billed"
          tone={usage.data!.dormantCount ? 'amber' : ''}
        />
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Registrations over time</h2>
        </div>
        <div className="card-body">
          <BarChart
            data={signups.data!.byDay.map((d) => ({ label: dateShort(d.day), value: d.count }))}
            height={200}
          />
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <h2>Revenue by plan</h2>
          </div>
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th className="num" title="Hotels subscribed to this plan">
                    Subscribers
                  </th>
                  <th className="num">Paying</th>
                  <th className="num">MRR</th>
                </tr>
              </thead>
              <tbody>
                {revenue.data!.byPlan.map((p) => (
                  <tr key={p.code}>
                    <td className="b">{p.name}</td>
                    <td className="num">{p.tenants}</td>
                    <td className="num">{p.paying}</td>
                    <td className="num b">{money0(p.mrr)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td className="num">{money0(revenue.data!.mrr)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Where signups come from</h2>
          </div>
          <div className="card-body">
            <BarList
              rows={signups.data!.bySource.map((s) => ({
                label: s.source === 'SELF_SERVICE' ? 'Registered themselves' : 'Created by SARTHI',
                value: s.count,
              }))}
              format={(v) => String(Math.round(v))}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Usage by hotel</h2>
          <span className="small muted">
            Invoices in the last 30 days — the earliest churn signal
          </span>
        </div>
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Hotel</th>
                <th>Plan</th>
                <th>Status</th>
                <th className="num">Rooms</th>
                <th className="num">Users</th>
                <th className="num">Invoices 30d</th>
                <th>Last invoice</th>
              </tr>
            </thead>
            <tbody>
              {usage.data!.entries.map((row) => (
                <tr key={row.tenantId}>
                  <td className="b">{row.name}</td>
                  <td>{row.planCode ?? '—'}</td>
                  <td>
                    <StatusChip status={row.status} />
                  </td>
                  <td className="num">{row.rooms}</td>
                  <td className="num">{row.users}</td>
                  <td className={`num ${row.dormant ? 'warn b' : ''}`}>{row.invoices30d}</td>
                  <td className="nowrap dim">
                    {row.lastInvoiceAt ? dateShort(row.lastInvoiceAt) : 'never'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Storage usage by hotel</h2>
            <span className="small muted">
              Estimated, not exact - one shared database backs every hotel, so this is a
              Postgres row-size estimate, cached from the last recompute.
              {storage.data!.neverComputedCount > 0
                ? ` ${storage.data!.neverComputedCount} hotel${storage.data!.neverComputedCount === 1 ? '' : 's'} not yet computed.`
                : ''}
            </span>
          </div>
          <button
            className="btn no-print"
            onClick={() => refreshStorage.mutate()}
            disabled={refreshStorage.isPending}
          >
            {refreshStorage.isPending ? 'Recomputing…' : 'Recompute now'}
          </button>
        </div>
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Hotel</th>
                <th className="num">Properties</th>
                <th className="num">Estimated storage</th>
                <th>Last computed</th>
              </tr>
            </thead>
            <tbody>
              {storage.data!.entries.map((row) => (
                <tr key={row.tenantId}>
                  <td className="b">{row.name}</td>
                  <td className="num">{row.properties.length}</td>
                  <td className="num">{bytes(row.totalBytes)}</td>
                  <td className="nowrap dim">
                    {row.computedAt ? dateTime(row.computedAt) : 'never computed'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2}>Total</td>
                <td className="num b">{bytes(storage.data!.totalBytes)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Coupon performance</h2>
          <span className="small muted">docs/platform/coupons.md</span>
        </div>
        <div className="card-body stack">
          <div className="kpis">
            <Kpi label="Coupons" value={String(couponReport.data!.counts.total)} />
            <Kpi
              label="Redeemed"
              value={String(couponReport.data!.counts.redeemed)}
              foot={`${couponReport.data!.redemptionRatePct}% redemption rate`}
              tone="blue"
            />
            <Kpi label="Free months granted" value={String(couponReport.data!.freeMonthsGranted)} />
            <Kpi
              label="Active"
              value={String(couponReport.data!.counts.active)}
              foot={`${couponReport.data!.counts.expired} expired · ${couponReport.data!.counts.exhausted} exhausted`}
            />
          </div>
          <div className="grid-2">
            <div>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Redemptions over time
              </div>
              <BarChart
                data={couponReport.data!.byDay.map((d) => ({ label: dateShort(d.day), value: d.count }))}
                height={180}
              />
            </div>
            <div>
              <div className="small muted" style={{ marginBottom: 8 }}>
                Top campaigns by redemption rate
              </div>
              <BarList
                rows={couponReport.data!.topCampaigns.map((c) => ({
                  label: `${c.name} (${c.redeemed}/${c.generated})`,
                  value: c.redemptionRatePct,
                }))}
                format={(v) => `${v}%`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
